export const EVENT_KINDS = ['meeting', 'announcement'] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

// Banner layout, independent of `iconKey`: either alignment may carry an icon.
export const EVENT_ALIGNMENTS = ['left', 'center'] as const;
export type EventAlignment = (typeof EVENT_ALIGNMENTS)[number];

// The exact JSON shape returned by `/api/v2/events/*`. Every event-facing
// surface consumes it: no field change without a contract update.
export interface EventDto {
    id: string;
    enabled: boolean;
    /** `EventKind`, typed loosely: an unknown value must not break a render. */
    kind: string;
    startsAt: string; // ISO 8601 UTC
    /** Hours. The authored length; endsAt/showUntil are derived from it server-side. */
    durationHours: number;
    endsAt: string | null;
    showFrom: string;
    showUntil: string;
    title: string;
    topic: string;
    badgeLabel: string;
    badgeTag: string | null;
    subtitle: string;
    /** Pop-up overrides; null inherits `title`/`subtitle` so old events are unchanged. */
    modalTitle: string | null;
    /** The pop-up's subtitle, authored as a rich document. Null falls back to
     *  the banner's plain `subtitle`. */
    modalSubtitleDoc: unknown;
    /** False hides the pop-up's subtitle block entirely. */
    modalShowSubtitle: boolean;
    /** Plain-text flattening of `bodyDoc` - read-only surfaces and the email. */
    body: string | null;
    /** The TipTap document authored in the admin rich editor. */
    bodyDoc: unknown;
    /** Practice ids this event is limited to. EMPTY MEANS EVERY PRACTICE.
     *  `/events/active` has already applied this server-side, so no rendering
     *  surface should re-test it - it is here for the admin list and form. */
    practices: string[];
    themePreset: string;
    /** `EventAlignment`, typed loosely for the same reason as `kind`. */
    alignment: string;
    iconKey: string | null;
    actionLabel: string;
    countdownToEvent: boolean;
    googleCalendarUrl: string | null;
    icsUrl: string | null;
    signedUp: boolean; // for the CALLING user; admin list returns false
}

// The resolved value of a `themePreset` key: colors for the banner, the
// `badgeTag` pill, the sub-line and the icon badge's drop shadow.
export interface EventThemePreset {
    /** Color only - `bannerBorder` carries the banner's divider. */
    readonly bg: string;
    readonly bannerBorder: string;
    readonly text: string;
    readonly accent: string;
    readonly accentText: string;
    readonly badgeBg: string;
    readonly badgeText: string;
    readonly subtitleText: string;
    readonly iconShadow: string;
}

/** Narrows a wire value; anything unknown renders as a meeting. */
export function toEventKind(kind: string): EventKind {
    return (EVENT_KINDS as readonly string[]).includes(kind) ? (kind as EventKind) : 'meeting';
}

// The single place any surface asks whether an event has RSVP behavior.
// Unknown/missing kind falls back to meeting behavior.
export function isAnnouncement(event: Pick<EventDto, 'kind'>): boolean {
    return event.kind === 'announcement';
}

/** Narrows a wire value; anything unknown falls back to the left layout. */
export function toEventAlignment(alignment: string | null | undefined): EventAlignment {
    return (EVENT_ALIGNMENTS as readonly string[]).includes(alignment ?? '')
        ? (alignment as EventAlignment)
        : 'left';
}
